// Auto-layout pre-pass: take a coordinate-free skeleton, run elkjs, and return a
// NEW skeleton with every node, group, arrow route, title and legend placed.
//
// Layered layout also routes the arrows: elk draws them at right angles around
// the boxes, with room left for their labels, and the compiler turns each route
// into an Excalidraw elbow arrow. Without this an arrow was a straight line
// between box centres and ran straight through any box in between.
//
// Used by `build`. `patch` does NOT auto-layout, because it has to preserve
// whatever the humans on the board have dragged around.

import ELK from "elkjs/lib/elk.bundled.js";
import { measure, sectionId, sizeOf } from "./excalidraw.js";
import { layoutLanes } from "./lanes.js";
import { layoutSequence } from "./sequence.js";

const ALGO = {
  layered: "org.eclipse.elk.layered", // flows, workflows, architecture (default)
  tree: "org.eclipse.elk.mrtree", // org charts, mind-maps
  mrtree: "org.eclipse.elk.mrtree",
  force: "org.eclipse.elk.force", // loose relationship graphs
  radial: "org.eclipse.elk.radial", // hub-and-spoke / mind-maps
  stress: "org.eclipse.elk.stress",
};
const DIR = { down: "DOWN", up: "UP", right: "RIGHT", left: "LEFT" };

export const LAYOUT_MODES = [...Object.keys(ALGO), "off", "manual"];
export const LAYOUT_DIRECTIONS = Object.keys(DIR);

const ORIGIN_X = 60;
const ORIGIN_Y = 120; // leaves room for the title above the diagram
const TITLE_Y = 40;
const GROUP_PADDING = "[top=48,left=24,bottom=24,right=24]"; // top clears the group's own label
const LEGEND_GAP = 60;

/** `defaults.node`, `defaults.edge` and `defaults.group` sit under each item's own fields. */
export const withDefaults = (skeleton) => {
  const d = skeleton.defaults ?? {};
  return {
    ...skeleton,
    nodes: (skeleton.nodes || []).map((n) => ({ ...d.node, ...n })),
    edges: (skeleton.edges || []).map((e) => ({ ...d.edge, ...e })),
    groups: (skeleton.groups || []).map((g) => ({ ...d.group, ...g })),
  };
};

const labelSize = (e) => {
  const m = measure(e.label, e.fontSize || 14);
  return { width: m.w + 12, height: m.h + 6 };
};

// A flow that loops back (retry, "not solved, try again") is broken at the
// back edge found by walking from the start, so the start stays first. The
// default breaks it elsewhere and puts the start in the middle of the picture.
// Groups need it too: elk does not pass it down.
const CYCLES = { "elk.layered.cycleBreaking.strategy": "DEPTH_FIRST" };

const runElk = async (skeleton, mode) => {
  const { nodes, groups, edges } = skeleton;
  const layered = mode === "layered";
  const children = (parent) => [
    ...groups
      .filter((g) => (g.parent ?? null) === parent)
      .map((g) => ({ id: g.id, layoutOptions: { "elk.padding": GROUP_PADDING, ...CYCLES }, children: children(g.id) })),
    ...nodes
      .filter((n) => (n.group ?? null) === parent)
      .map((n) => {
        const s = sizeOf(n);
        return { id: n.id, width: s.w, height: s.h };
      }),
  ];
  const graph = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": ALGO[mode] || ALGO.layered,
      "elk.direction": DIR[skeleton.direction] || "DOWN",
      "elk.spacing.nodeNode": String(skeleton.spacing?.node ?? 60),
      "elk.layered.spacing.nodeNodeBetweenLayers": String(skeleton.spacing?.layer ?? 90),
      "elk.spacing.edgeNode": "30",
      "elk.spacing.edgeEdge": "20",
      "elk.layered.spacing.edgeEdgeBetweenLayers": "20",
      "elk.layered.spacing.edgeNodeBetweenLayers": "30",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      ...CYCLES,
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.edgeLabels.placement": "CENTER",
      "elk.hierarchyHandling": "INCLUDE_CHILDREN",
      // Every coordinate comes back absolute, nested groups included.
      "elk.json.edgeCoords": "ROOT",
      "elk.json.shapeCoords": "ROOT",
    },
    children: children(null),
    edges: edges.map((e, i) => ({
      id: `e${i}`,
      sources: [e.from],
      targets: [e.to],
      // Inline: on the line, where Excalidraw draws an arrow's label.
      labels: layered && e.label ? [{ text: e.label, ...labelSize(e), layoutOptions: { "elk.edgeLabels.inline": "true" } }] : [],
    })),
  };
  const res = await new ELK().layout(graph);

  const boxes = new Map();
  const collect = (n) => {
    for (const c of n.children || []) {
      boxes.set(c.id, { x: c.x, y: c.y, w: c.width, h: c.height });
      collect(c);
    }
  };
  collect(res);
  const routes = new Map(
    (res.edges || [])
      .filter((e) => layered && e.sections?.length === 1)
      .map((e) => {
        const s = e.sections[0];
        return [e.id, [s.startPoint, ...(s.bendPoints || []), s.endPoint].map((p) => [p.x, p.y])];
      }),
  );
  // The middle of the space elk kept free for each label.
  const labels = new Map(
    (res.edges || [])
      .filter((e) => routes.has(e.id) && e.labels?.[0]?.x != null)
      .map((e) => {
        const l = e.labels[0];
        return [e.id, [l.x + l.width / 2, l.y + l.height / 2]];
      }),
  );
  return { boxes, routes, labels };
};

const bounds = (items) => {
  const xs = items.flatMap((i) => [i.x, i.x + i.w]);
  const ys = items.flatMap((i) => [i.y, i.y + i.h]);
  return { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) };
};

/** The legend as a row of small labelled boxes under the diagram. */
const legendNodes = (legend, under, prefix) => {
  let x = under.left;
  const y = under.bottom + LEGEND_GAP + 28;
  return legend.map((item, i) => {
    const node = { type: "rectangle", fontSize: 14, ...item, id: `${prefix}legend__${i}`, role: "legend" };
    const { w } = sizeOf(node);
    const placed = { ...node, x, y, w, h: 36 };
    x += w + 16;
    return placed;
  });
};

/** Title and legend go where the diagram leaves room for them. */
const placeExtras = (skeleton) => {
  const placed = [...skeleton.nodes, ...skeleton.groups].filter((n) => Number.isFinite(n.x) && Number.isFinite(n.y));
  if (!placed.length) return skeleton;
  const box = bounds(placed.map((n) => ({ x: n.x, y: n.y, w: n.w ?? sizeOf(n).w, h: n.h ?? sizeOf(n).h })));
  const texts = [...(skeleton.texts || [])];
  // Each section has its own title and legend, so their ids carry the section.
  const prefix = skeleton.section ? `${sectionId(skeleton.section)}__` : "";
  if (skeleton.title) {
    texts.unshift({ id: `${prefix}title`, text: skeleton.title, x: box.left, y: Math.min(TITLE_Y, box.top - 70), fontSize: 28, role: "title" });
  }
  const legend = skeleton.legend?.length ? legendNodes(skeleton.legend, box, prefix) : [];
  if (legend.length) {
    texts.push({ id: `${prefix}legend__title`, text: "Legend", x: box.left, y: box.bottom + LEGEND_GAP, fontSize: 16, role: "legend" });
  }
  return { ...skeleton, nodes: [...skeleton.nodes, ...legend], texts };
};

export const layoutSkeleton = async (input) => {
  // A sequence diagram has its own geometry: columns by participant, rows by message.
  if (input.participants) return placeExtras(layoutSequence(input, { x: ORIGIN_X, y: ORIGIN_Y }));
  const skeleton = withDefaults(input);
  if (skeleton.lanes) {
    const { lanes, ...rest } = skeleton; // the lanes are groups from here on
    return placeExtras({ ...rest, ...layoutLanes(skeleton, [ORIGIN_X, ORIGIN_Y]) });
  }
  const mode = skeleton.layout ?? "layered";
  if (mode === "off" || mode === "manual" || skeleton.nodes.length === 0) return placeExtras(skeleton);

  const { boxes, routes, labels } = await runElk(skeleton, mode);

  // Normalize to a positive origin (force/radial can emit negatives).
  const all = [...boxes.values()];
  const dx = ORIGIN_X - Math.min(...all.map((b) => b.x));
  const dy = ORIGIN_Y - Math.min(...all.map((b) => b.y));
  const at = (id, item) => {
    const b = boxes.get(id);
    return b
      ? { ...item, x: Math.round(b.x + dx), y: Math.round(b.y + dy), w: Math.round(b.w), h: Math.round(b.h) }
      : item;
  };

  return placeExtras({
    ...skeleton,
    nodes: skeleton.nodes.map((n) => at(n.id, n)),
    groups: skeleton.groups.map((g) => at(g.id, g)),
    // Only layered layout routes arrows; the others read best with straight lines.
    edges: skeleton.edges.map((e, i) =>
      routes.has(`e${i}`) && e.style !== "straight"
        ? {
            ...e,
            route: routes.get(`e${i}`).map(([x, y]) => [Math.round(x + dx), Math.round(y + dy)]),
            ...(labels.has(`e${i}`) ? { labelAt: labels.get(`e${i}`).map((v, k) => Math.round(v + (k ? dy : dx))) } : {}),
          }
        : { style: mode === "layered" ? undefined : "straight", ...e },
    ),
  });
};
